/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCallback, useId, useState } from "react";
import { TabsContext, TabsContextValue } from "./tabs.context";

export interface CosmosTabsProps {
	/** Controlled active tab index. May be undefined when canToggleTabs is true. */
	activeIndex?: number;
	/** Uncontrolled initial active tab index. */
	defaultIndex?: number;
	/** Enables controlled mode when provided. */
	onActiveIndexChange?: (index: number | undefined) => void;
	/** Clicking the active tab deselects it, emitting undefined. */
	canToggleTabs?: boolean;
	/** Size of tab buttons */
	size?: "sm" | "md" | "lg";
	/** Variant for active tabs */
	activeVariant?: string;
	/** Variant for inactive tabs */
	inactiveVariant?: string;
	/** Child components */
	children: React.ReactNode;
}

/**
 * Root container that manages tab state. Expects to wrap Tabs.ButtonList and Tabs.Panel components.
 */
export const CosmosTabs = ({
	activeIndex: controlledIndex,
	defaultIndex,
	onActiveIndexChange,
	canToggleTabs = false,
	size = "md",
	activeVariant = "contrast",
	inactiveVariant = "secondary",
	children,
}: CosmosTabsProps): React.JSX.Element => {
	// Can't use `activeIndex !== undefined` here because activeIndex may be undefined if canToggleTabs
	const isControlled = onActiveIndexChange !== undefined;

	if (process.env.NODE_ENV !== "production") {
		if (isControlled && defaultIndex !== undefined) {
			console.warn("CosmosTabs: `defaultIndex` is ignored when `onActiveIndexChange` is provided.");
		}
	}

	const [uncontrolledIndex, setUncontrolledIndex] = useState<number | undefined>(
		defaultIndex ?? (canToggleTabs ? undefined : 0),
	);

	const [hasTabsList, setHasTabsList] = useState(false);
	const idPrefix = useId();
	const [panelCount, setPanelCount] = useState(0);
	const registerPanel = useCallback((): (() => void) => {
		setPanelCount((count) => count + 1);
		return () => setPanelCount((count) => count - 1);
	}, []);

	const activeIndex = isControlled ? controlledIndex : uncontrolledIndex;

	const handleIndexChange = (newIndex: number): void => {
		const next = canToggleTabs && activeIndex === newIndex ? undefined : newIndex;

		if (!isControlled) setUncontrolledIndex(next);
		onActiveIndexChange?.(next);
	};

	const contextValue: TabsContextValue = {
		activeIndex,
		onIndexChange: handleIndexChange,
		canToggleTabs,
		size,
		activeVariant,
		inactiveVariant,
		hasTabsList,
		setHasTabsList,
		hasActivePanel: panelCount > 0,
		registerPanel,
		idPrefix,
	};

	return <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>;
};

CosmosTabs.displayName = "CosmosTabs";
